<template>
    <VModalWrapper :title="title" :is-loading="isLoading" v-on="listeners" v-bind="attributes">
        <ValidationObserver v-slot="{ handleSubmit }">
            <form class="active-form" @submit.prevent="handleSubmit(submit)">
                <FormFieldConstructor v-model="fields" :dictionaries="dictionaries"></FormFieldConstructor>

                <span v-if="errorMessage" class="active-form__error">{{ errorMessage }}</span>

                <div class="active-form__actions">
                    <VButton type="button" class="active-form__action button_green-light" @click="hide">Отмена</VButton>
                    <VButton type="submit" class="active-form__action button_green" :disabled="isLoading || loadFailed">
                        Сохранить форму
                    </VButton>
                </div>
            </form>
        </ValidationObserver>
    </VModalWrapper>
</template>
<script>
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import VButton from '../components/VButton/VButton.vue';
import VModalWrapper from '../components/VModalWrapper/VModalWrapper.vue';
import FormFieldConstructor from '../components/FormFieldConstructor/FormFieldConstructor.vue';
import ModalNames from '../../js/enums/ModalNames';
import ModalMixin from '../../js/vue/mixins/ModalMixin';
import RequestServiceFormFieldType from '../../js/enums/RequestServiceFormFieldType';
import { getServiceFormFields, replaceServiceFormFields, getDictionaryList } from '../../js/admin-api';
import { errorMessage } from '../../js/error';

@Component({
    components: {
        VButton,
        VModalWrapper,
        FormFieldConstructor,
        ValidationObserver,
    },
})
class VServiceFormFieldsCard extends ModalMixin {
    @Prop({ type: String, default: ModalNames.ADMIN_PANEL_SERVICE_FORM_FIELDS_CARD })
    name;
    @Prop({ type: String, default: 'Форма заявки' })
    defaultTitle;

    serviceId = null;
    fields = [];
    dictionaries = [];
    isLoading = true;
    loadFailed = false;
    errorMessage = '';

    /**
     * Shapes each row into exactly what PUT /admin/services/:id/form-fields expects. A select's options
     * come from exactly one source: a linked dictionary (dictionaryId set, settings null) or its own
     * hand-entered rows (settings.options set, dictionaryId null). Every other type carries neither, so
     * the request can never trip the backend rules that forbid options/dictionaries on those types.
     */
    get payload() {
        return this.fields.map((field) => {
            const isSelect =
                field.type === RequestServiceFormFieldType.SELECT ||
                field.type === RequestServiceFormFieldType.MULTISELECT;
            const usesDictionary = isSelect && Boolean(field.dictionaryId);

            return {
                id: field.id ?? null,
                type: field.type,
                label: field.label,
                key: field.key,
                required: Boolean(field.required),
                dictionaryId: usesDictionary ? field.dictionaryId : null,
                settings:
                    isSelect && !usesDictionary ? { options: (field.settings && field.settings.options) || [] } : null,
            };
        });
    }

    /**
     * A select must draw its options from somewhere. vee-validate covers the hand-entered rows (each is
     * `required`), but the dictionary picker is not a validated field, so guard here: a select with no
     * dictionary and no options would otherwise only fail at the server.
     *
     * @returns {string|null} the label of the first select missing a source, or null when all are fine.
     */
    selectWithoutSource() {
        const field = this.fields.find((field) => {
            const isSelect =
                field.type === RequestServiceFormFieldType.SELECT ||
                field.type === RequestServiceFormFieldType.MULTISELECT;

            if (!isSelect) {
                return false;
            }

            const hasOptions = Boolean(field.settings && field.settings.options && field.settings.options.length);

            return !field.dictionaryId && !hasOptions;
        });

        return field ? field.label || 'без названия' : null;
    }

    /**
     * Field keys must be unique (the backend enforces `distinct`). vee-validate validates each field
     * in isolation and cannot see its siblings, so this cross-field check lives here.
     *
     * @returns {string|null} the first duplicated key, or null when all are unique.
     */
    duplicateKey() {
        const keys = this.fields.map((field) => field.key).filter((key) => key);
        const seen = new Set();

        for (const key of keys) {
            if (seen.has(key)) {
                return key;
            }

            seen.add(key);
        }

        return null;
    }

    async submit() {
        // If the initial load failed, `fields` is still the empty default. Saving it would PUT an
        // empty set and wipe every field the service already has. Refuse and close instead.
        if (this.loadFailed) {
            this.hide();
            return;
        }

        const duplicate = this.duplicateKey();
        if (duplicate) {
            this.errorMessage = `Ключ «${duplicate}» повторяется — ключи полей должны быть уникальными.`;
            return;
        }

        const noSource = this.selectWithoutSource();
        if (noSource) {
            this.errorMessage = `Для поля «${noSource}» задайте варианты значений или выберите справочник.`;
            return;
        }

        this.errorMessage = '';
        this.isLoading = true;

        try {
            await replaceServiceFormFields(this.serviceId, this.payload);
            this.resolve(this.fields);
            this.hide();
        } catch (error) {
            this.errorMessage = errorMessage(error);
        } finally {
            this.isLoading = false;
        }
    }

    async beforeOpen(event) {
        this.serviceId = event?.params?.serviceId;
        this.isLoading = true;
        this.loadFailed = false;
        this.errorMessage = '';
        this.dictionaries = [];

        try {
            const response = await getServiceFormFields(this.serviceId);
            this.fields = response.data;
        } catch (error) {
            // Mark the load as failed so submit() cannot overwrite the service's real fields with the
            // empty default, and close the card once the error has been shown.
            this.loadFailed = true;
            this.$showModal(ModalNames.ERROR, { errorMessage: errorMessage(error) });
            this.hide();
            this.isLoading = false;
            return;
        }

        // Dictionaries only feed the optional "Справочник" source, so a failure here must not block
        // editing the form's fields — degrade to an empty list (hand-entered options still work).
        try {
            const response = await getDictionaryList();
            this.dictionaries = response.data;
        } catch (error) {
            this.dictionaries = [];
        }

        this.isLoading = false;
    }

    beforeClose() {
        this.fields = [];
        this.dictionaries = [];
        this.serviceId = null;
        this.loadFailed = false;
        this.errorMessage = '';
    }
}

export default VServiceFormFieldsCard;
</script>
